import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCardTerminalReconciliation } from '@/hooks/useCardTerminalReconciliation';
import { formatCentsForDisplay } from '@/utils/money';

export function CardTerminalExceptions() {
  const { t, i18n } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const query = useCardTerminalReconciliation();
  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('terminalReconciliation.title')}
      </Typography>
      <Typography color="text.secondary" mb={3}>
        {t('terminalReconciliation.subtitle')}
      </Typography>
      {query.isPending ? (
        <CircularProgress aria-label={tc('loading')} />
      ) : query.isError ? (
        <Alert severity="error">{tc('error')}</Alert>
      ) : !query.data?.exceptions.length ? (
        <Alert severity="success">{t('terminalReconciliation.empty')}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label={t('terminalReconciliation.title')}>
            <TableHead>
              <TableRow>
                {['day', 'kind', 'transaction', 'invoice', 'amount'].map((column) => (
                  <TableCell key={column}>{t(`terminalReconciliation.${column}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.exceptions.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.reconciliationDate}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={row.severity === 'info' ? 'info' : 'error'}
                      label={t(`terminalReconciliation.kinds.${row.kind}`)}
                    />
                  </TableCell>
                  <TableCell>
                    {row.transactionCode ?? t('terminalReconciliation.unknown')}
                  </TableCell>
                  <TableCell>
                    {row.invoiceId ? (
                      <Link component={RouterLink} to={`/invoices/${row.invoiceId}`}>
                        {t('terminalReconciliation.openInvoice')}
                      </Link>
                    ) : (
                      t('terminalReconciliation.unknown')
                    )}
                  </TableCell>
                  <TableCell>
                    {row.currency === 'EUR'
                      ? formatCentsForDisplay(row.amountCents)
                      : new Intl.NumberFormat(i18n.language, {
                          style: 'currency',
                          currency: row.currency,
                        }).format(row.amountCents / 100)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
